import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { Briefcase, Clock, CheckCircle, XCircle, ArrowRight, Star, Calendar, MapPin, DollarSign } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const WorkTrialsPage = () => {
  const { user } = useAuth();
  const [trials, setTrials] = useState([]);
  const [stats, setStats] = useState({ available: 0, active: 0, completed: 0, avg_rating: 0 });
  const [activeTab, setActiveTab] = useState('available');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadTrials(); }, [user]);

  const loadTrials = async () => {
    try {
      const data = await api.getWorkTrials();
      setTrials(data.trials || []);
      setStats(data.stats || stats);
    } catch {
      setTrials([
        { trial_id: '1', title: 'ML Pipeline Development', company: 'TechCorp AI', company_logo: 'https://ui-avatars.com/api/?name=TechCorp+AI&background=6366F1&color=fff', duration: '3 days', pay: '$500', description: 'Build a basic ML data pipeline.', skills: ['Python','Apache Spark','ML'], location: 'Remote', deadline: 'Starts Feb 15', status: 'available', match_score: 95 },
        { trial_id: '2', title: 'Frontend Component Library', company: 'DataMinds', company_logo: 'https://ui-avatars.com/api/?name=DataMinds&background=8B5CF6&color=fff', duration: '5 days', pay: '$800', description: 'Create reusable React components.', skills: ['React','TypeScript','CSS'], location: 'Remote', deadline: 'Starts Feb 18', status: 'available', match_score: 88 },
        { trial_id: '3', title: 'API Integration Project', company: 'AI Research Lab', company_logo: 'https://ui-avatars.com/api/?name=AI+Research+Lab&background=10B981&color=fff', duration: '2 days', pay: '$300', description: 'Integrate third-party APIs.', skills: ['Node.js','REST APIs','MongoDB'], location: 'Remote', deadline: 'Completed', status: 'completed', result: 'passed', feedback: 'Excellent work!', rating: 5 },
        { trial_id: '4', title: 'Data Visualization Dashboard', company: 'AnalyticsPro', company_logo: 'https://ui-avatars.com/api/?name=AnalyticsPro&background=F59E0B&color=fff', duration: '4 days', pay: '$600', description: 'Build an interactive dashboard.', skills: ['React','D3.js','Python'], location: 'Remote', deadline: 'In progress', status: 'active', days_left: 2, progress: 60 },
      ]);
      setStats({ available: 2, active: 1, completed: 1, avg_rating: 5.0 });
    } finally { setLoading(false); }
  };

  const handleAccept = async (trial) => {
    try { await api.acceptTrial(trial.trial_id); } catch {}
    setTrials(trials.map(t => t.trial_id === trial.trial_id ? { ...t, status: 'active' } : t));
    toast({ title: 'Trial accepted!', description: `You've been enrolled in "${trial.title}" with ${trial.company}. Good luck!` });
  };

  const filtered = trials.filter(t => {
    if (activeTab === 'available') return t.status === 'available';
    if (activeTab === 'active') return t.status === 'active';
    if (activeTab === 'completed') return t.status === 'completed';
    return true;
  });

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
          <div className="mb-8"><h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Work Trials</h1><p className="text-gray-600 text-sm sm:text-base">Short-term paid projects to showcase your skills and get hired</p></div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="p-6 text-center"><Briefcase className="w-8 h-8 text-[#0A3900] mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.available}</div><div className="text-sm text-gray-600">Available Trials</div></Card>
            <Card className="p-6 text-center"><Clock className="w-8 h-8 text-[#0A3900] mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.active}</div><div className="text-sm text-gray-600">In Progress</div></Card>
            <Card className="p-6 text-center"><CheckCircle className="w-8 h-8 text-green-600 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.completed}</div><div className="text-sm text-gray-600">Completed</div></Card>
            <Card className="p-6 text-center"><Star className="w-8 h-8 text-yellow-500 mx-auto mb-2" /><div className="text-2xl font-bold text-gray-900 mb-1">{stats.avg_rating}</div><div className="text-sm text-gray-600">Avg Rating</div></Card>
          </div>

          <div className="flex gap-2 mb-6">
            {['available', 'active', 'completed'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? 'bg-[#0A3900] text-white' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>{tab.charAt(0).toUpperCase() + tab.slice(1)}</button>
            ))}
          </div>

          <div className="space-y-4">
            {filtered.map(trial => (
              <Card key={trial.trial_id} className="p-4 sm:p-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <img src={trial.company_logo} alt={trial.company} className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                          <h3 className="text-base sm:text-xl font-semibold text-gray-900">{trial.title}</h3>
                          {trial.match_score && <Badge className="bg-[#E0E8D0] text-[#0A3900]">{trial.match_score}% match</Badge>}
                        </div>
                        <p className="text-gray-600 text-sm sm:text-base">{trial.company}</p>
                      </div>
                      {trial.status === 'available' && (
                        <Button onClick={() => handleAccept(trial)} className="bg-[#0A3900] hover:bg-[#1A5C0A] w-full sm:w-auto text-sm">Accept Trial <ArrowRight className="w-4 h-4 ml-2" /></Button>
                      )}
                    </div>
                    <p className="text-gray-700 mb-3 text-sm sm:text-base">{trial.description}</p>
                    <div className="flex flex-wrap gap-2 mb-3">{trial.skills.map((s, i) => (<span key={i} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-full">{s}</span>))}</div>
                    <div className="flex flex-wrap items-center gap-3 sm:gap-6 text-xs sm:text-sm text-gray-500">
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{trial.duration}</span>
                      <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />{trial.pay}</span>
                      <span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{trial.location}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />{trial.deadline}</span>
                    </div>
                    {trial.status === 'active' && trial.progress !== undefined && (
                      <div className="mt-4 p-4 bg-[#F1F5E6] rounded-lg">
                        <div className="flex items-center justify-between mb-2"><span className="text-sm font-medium text-[#0A3900]">Progress</span><span className="text-sm font-medium text-[#0A3900]">{trial.progress}% - {trial.days_left} days left</span></div>
                        <div className="w-full bg-[#B8C4AD] rounded-full h-2"><div className="bg-[#0A3900] h-2 rounded-full" style={{ width: `${trial.progress}%` }} /></div>
                      </div>
                    )}
                    {trial.status === 'completed' && (
                      <div className={`mt-4 p-4 rounded-lg ${trial.result === 'passed' ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {trial.result === 'passed' ? <CheckCircle className="w-5 h-5 text-green-600" /> : <XCircle className="w-5 h-5 text-red-600" />}
                          <span className={`font-semibold ${trial.result === 'passed' ? 'text-green-800' : 'text-red-800'}`}>{trial.result === 'passed' ? 'Trial Passed!' : 'Trial Not Passed'}</span>
                          <div className="flex items-center gap-1 ml-auto">{[...Array(trial.rating || 0)].map((_, i) => (<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />))}</div>
                        </div>
                        {trial.feedback && <p className="text-sm text-gray-700">{trial.feedback}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
            {filtered.length === 0 && <Card className="p-12 text-center"><Briefcase className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-xl font-semibold text-gray-900 mb-2">No {activeTab} trials</h3><p className="text-gray-600">Check back later for new opportunities.</p></Card>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkTrialsPage;
